import { useState } from "react";
import { useForm } from "react-hook-form";
import type { RequestState } from "@redux/state/shared-types";
import { FormCard } from "@shared-kernel/primary/shared/form-card/form-card";
import { FormSaveActions } from "@shared-kernel/primary/shared/form-save-actions/form-save-actions";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";

interface DemoFormInputs {
  name: string;
}

const defaultValues: DemoFormInputs = { name: "Marie Dupont" };

const SIMULATED_LATENCY_MS = 1200;

export const ComponentTestPage = () => {
  const [processing, setProcessing] = useState<RequestState>("idle");
  const [savedSnapshot, setSavedSnapshot] = useState<DemoFormInputs>(defaultValues);

  const methods = useForm<DemoFormInputs>({ defaultValues });
  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = methods;

  const handleOnSubmit = async (values: DemoFormInputs) => {
    setProcessing("pending");
    await new Promise(resolve => setTimeout(resolve, SIMULATED_LATENCY_MS));
    setProcessing("success");
    setSavedSnapshot(values);
    reset(values);
  };

  const handleCancel = () => reset(savedSnapshot);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">FormSaveActions — banc d&apos;essai</h1>
        <p className="text-sm text-muted-foreground">
          État actuel : <code>processing={processing}</code> · <code>isDirty={String(isDirty)}</code> ·{" "}
          <code>isSubmitting={String(isSubmitting)}</code>
        </p>
      </header>

      <FormCard>
        <Form {...methods}>
          <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
            <FormField
              control={methods.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormSaveActions processing={processing} isDirty={isDirty} isSubmitting={isSubmitting} onCancel={handleCancel} />
          </form>
        </Form>
      </FormCard>

      <section className="rounded-md border border-dashed p-4 text-sm">
        <h2 className="mb-2 font-medium">Scénarios à observer</h2>
        <ol className="list-inside list-decimal space-y-1 text-muted-foreground">
          <li>Au chargement : les deux boutons sont désactivés (form pristine).</li>
          <li>Modifier le champ : les deux boutons s&apos;activent.</li>
          <li>Cliquer « Enregistrer » : spinner + « Enregistrement… » pendant ~1,2 s.</li>
          <li>Après la requête : bouton vert avec icône Check + « Enregistré » pendant 2 s, puis retour à l&apos;état désactivé.</li>
          <li>Modifier le champ pendant le state success : retour immédiat à « Enregistrer » actif.</li>
          <li>Cliquer « Annuler » sur un form dirty : retourne au dernier snapshot enregistré.</li>
        </ol>
      </section>
    </div>
  );
};
